import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { firstValueFrom, pipe, switchMap, tap } from 'rxjs';
import DataSource from 'devextreme/data/data_source';
import { CustomStore, LoadOptions } from 'devextreme/common/data';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  AcademyCertificatesState,
  initialAcademyCertificatesState,
} from './entities/academy-certificates-store.model';
import { IssuedCertificateItem } from './entities/academy-responses.model';
import {
  ACADEMY_ENUM_WIRE_CODES,
  CertificateTargetType,
} from './entities/academy-enums.model';
import { CertificatesService } from './infra/certificates.service';

const ALL_OPS = ['loadMine', 'verify'] as const;

/** Grid page size for the server-paged issued-certificates DataSource. */
const CERTIFICATES_PAGE_SIZE = 10;

const TARGET_TYPE_FIELD = 'CertificateTargetType';

// Own keys only (a prototype key such as `constructor` is not a type). Not `Object.hasOwn`: it
// needs Chrome 93, and Android WebView 91 still reaches these routes with no polyfill for it
// (see `libs/shared/polyfills/legacy-apis.ts`).
const isTargetTypeName = (value: unknown): value is CertificateTargetType =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(
    ACADEMY_ENUM_WIRE_CODES[TARGET_TYPE_FIELD],
    value
  );

/**
 * The Tipo header-filter selection, read from the DX filter handed to `CustomStore.load`.
 * Measured against a real dxDataGrid, `lib-dx-data-grid` wraps the chip —
 * `[['CertificateTargetType', '=', 'Course']]` — and `and`-combines it with the search-panel
 * clause when there is one (`[['PersonName', 'contains', …], 'and', [[…]]]`).
 *
 * `GET /Certificates/issued` takes ONE category, so the only representable shape is a single
 * `=` on the column, not under `'!'`, not inside an `or` group, with a known enum name. Any
 * other expression naming the column THROWS, so the load rejects and no request goes out with a
 * wrong or partial parameter — e.g. a multiselect chip OR-ing both types would otherwise send
 * only the first. Clauses on other columns are ignored. No clause on the column → `undefined`.
 *
 * That guarantee stops at the store. In the UI, `lib-dx-data-grid` only reacts to data errors
 * that carry an `httpStatus`, so a rejected load currently leaves the PREVIOUS rows visible under
 * the new chip, with no message. The real protection is the page keeping the Tipo group
 * single-select (`dropdown`), pinned by a test in the page's real-template spec.
 */
const selectedTargetType = (
  filter: unknown
): CertificateTargetType | undefined => {
  let selected: CertificateTargetType | undefined;
  const walk = (node: unknown, negatedOrOr: boolean): void => {
    if (!Array.isArray(node)) return;
    if (node[0] === '!') return walk(node[1], true);
    if (typeof node[0] === 'string') {
      // A leaf `[field, operator, value]`; only the category column is inspected.
      if (node[0] !== TARGET_TYPE_FIELD) return;
      const single = !negatedOrOr && !selected && node.length === 3;
      if (!single || node[1] !== '=' || !isTargetTypeName(node[2])) {
        throw new Error(
          `GET /Certificates/issued filters by ONE certificate type; cannot represent the grid filter ${JSON.stringify(filter)}`
        );
      }
      selected = node[2];
      return;
    }
    const inOr =
      negatedOrOr ||
      node.some((part) => String(part).toLowerCase() === 'or');
    for (const part of node) walk(part, inOr);
  };
  walk(filter, false);
  return selected;
};

export const AcademyCertificatesStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyCertificatesState>(initialAcademyCertificatesState),
  withReqState(...ALL_OPS),
  withMethods(
    (
      store,
      certificates = inject(CertificatesService),
      errorStore = inject(ErrorStore)
    ) => {
      /**
       * Server-paged issued-certificates DataSource for `lib-dx-data-grid`, backed by the
       * producer endpoint `GET /Certificates/issued`. DX paging (`take`/`skip`) drives
       * `top`/`skip`; the search term (holder name) is read through `getSearch` on every
       * `reload()`. The Tipo header filter reaches `options.filter` and is read by
       * {@link selectedTargetType} (a filter the endpoint cannot represent rejects the load);
       * nothing else in `options.filter` is forwarded (the search clause DX adds is superseded
       * by `getSearch`).
       * `options.sort` is NOT forwarded: the endpoint has no sort parameter (fixed order
       * `IssuedOnUtc` desc, `PersonName` asc), so the page disables sorting. `Total` is the
       * grid `totalCount`. Rows keep NAMED enums (`toMineResult` applies `fromWireEnums`).
       */
      const getCertificatesDataSource = (
        getSearch: () => string = () => ''
      ): DataSource<IssuedCertificateItem> =>
        new DataSource<IssuedCertificateItem>({
          paginate: true,
          pageSize: CERTIFICATES_PAGE_SIZE,
          store: new CustomStore<IssuedCertificateItem>({
            key: 'CertificateId',
            // `async`: an unrepresentable Tipo filter throws before any request, and the throw
            // becomes a rejected load (DevExtreme's `dataErrorOccurred`) — never swallowed by the
            // HTTP catch. The grid shows no message for it today; see `selectedTargetType`.
            load: async (options: LoadOptions<IssuedCertificateItem>) => {
              const certificateTargetType = selectedTargetType(options.filter);
              return firstValueFrom(
                certificates.issued({
                  search: getSearch(),
                  certificateTargetType,
                  top: options.take,
                  skip: options.skip,
                })
              )
                .then((res) => ({ data: res.items, totalCount: res.total }))
                .catch((error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  return { data: [], totalCount: 0 };
                });
            },
          }),
        });

      const loadMine = rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadMine'))),
          switchMap(() =>
            certificates.mine().pipe(
              tapResponse({
                next: (mine) =>
                  patchState(store, { mine }, setPropLoaded('loadMine')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadMine', error));
                },
              })
            )
          )
        )
      );

      const verify = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('verify'))),
          switchMap((code) =>
            certificates.verify(code).pipe(
              tapResponse({
                next: (result) =>
                  patchState(
                    store,
                    {
                      verificationByCode: {
                        ...store.verificationByCode(),
                        [code]: result,
                      },
                    },
                    setPropLoaded('verify')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('verify', error));
                },
              })
            )
          )
        )
      );

      const reset = (): void =>
        patchState(
          store,
          initialAcademyCertificatesState,
          setPropInit(...ALL_OPS)
        );

      return { getCertificatesDataSource, loadMine, verify, reset };
    }
  )
);
